import { type UscDefaultInsuranceQuoteType, UscContext } from "../../shared-logic/types/UscCommonTypes";
import { type KeyValueArrayType, formatPriceIntl, replaceKeysInFormat } from "../../../common-deprecated/priceUtils";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { getAemInsuranceRateFormat } from "../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import type { AemFmComponent } from "../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { isPersonalizedUscInsurance } from "./settings";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";

// ----------------------------------------------------------------------
//  Personalized insurance
// ----------------------------------------------------------------------
export type UscInsuranceQuoteType = {
    quoteId: string;
    calculationId: string;
    premium: number;
    disclaimer: string;
    insuranceType: string;

    // TES
    code?: string;
    description?: string;
    descriptionList?: [string, string[]];

    // TFR
    bonusMalus?: number;
    damageExcess?: number;
    monthlyPrice?: number;
    windowBreakage?: number;
};

export const defaultUscInsuranceQuote: UscInsuranceQuoteType = {
    quoteId: "",
    calculationId: "",
    premium: 0,
    disclaimer: "",
    insuranceType: "",
};

/**
 * These keys correspond to UscInsuranceQuoteType's properties, and also
 * what we receive from Makolab.
 */
export enum InsurancePriceLabelKey {
    Price = "price",
    CalculationId = "calculationId",
    InsuranceType = "insuranceType",
    Disclaimer = "disclaimer",
}

export type FormatInsurancePriceReturnType = {
    insurancePriceLabels: KeyValueArrayType<InsurancePriceLabelKey>;
    disclaimer?: string;
};

/**
 * Formats an insurance quote retrieved from the insurance calculator or quoteId
 * using the configured format in the Flexibility Matrix.
 */
export const formatUscInsurancePrice = (
    commonSettings: CommonSettingsType,
    uscSettings: UscSettingsType,
    insuranceQuote: UscInsuranceQuoteType | UscDefaultInsuranceQuoteType | null,
    insurancePriceFormat: string,
): FormatInsurancePriceReturnType => {
    if (!insuranceQuote) {
        return {
            insurancePriceLabels: [],
        };
    }

    const input = uscSettings.insuranceIsYearly ? insuranceQuote.premium : insuranceQuote.monthlyPrice || 0;

    if (!input) return { insurancePriceLabels: [] };

    const decimals = input.toString().split(".")[1]?.length || 0;

    // insurancePriceFormat for insurance prices looks like this:
    // [price]{value}[/price][calculationId]Calculation ID: {value}[/calculationId]...
    // availableReplacements contains a mapping of the tag and the value (if any) that needs to be replace in the tag.
    const insuranceInfoReplacement: Record<InsurancePriceLabelKey, string> = {
        price: formatPriceIntl(commonSettings, input, {
            // TIS requested that we should always respect the number of decimals returned by the calculator.
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).primaryPrice,
        calculationId:
            isPersonalizedUscInsurance(insuranceQuote) && insuranceQuote.calculationId
                ? insuranceQuote.calculationId
                : "",
        insuranceType: (isPersonalizedUscInsurance(insuranceQuote) && insuranceQuote.insuranceType) || "",
        disclaimer: insuranceQuote.disclaimer,
    };

    return {
        insurancePriceLabels: replaceKeysInFormat(insuranceInfoReplacement, insurancePriceFormat),
        disclaimer: insuranceQuote.disclaimer,
    };
};

/**
 * Helper function for formatUscInsurancePrice which
 * instead of returning an array containing objects,
 * we return one object where the key is the label key.
 * This saves us from having to use .find() in components
 * where we only need one or two labels.
 * @note This returns an object with all the labels, even if they are empty.
 *       Handle this in the component where you use this function.
 */
export const formatUscInsurancePriceAsObject = (
    commonSettings: CommonSettingsType,
    uscSettings: UscSettingsType,
    insuranceQuote: UscInsuranceQuoteType | UscDefaultInsuranceQuoteType | null,
    insurancePriceFormat: string,
): { [key in InsurancePriceLabelKey]: string } => {
    const { insurancePriceLabels } = formatUscInsurancePrice(
        commonSettings,
        uscSettings,
        insuranceQuote,
        insurancePriceFormat,
    );

    return insurancePriceLabels.reduce(
        (acc, label) => {
            acc[label.key] = label.value;
            return acc;
        },
        {} as { [key in InsurancePriceLabelKey]: string },
    );
};

// ----------------------------------------------------------------------
// Utility functions to make it easier to retrieve a specific label
// ----------------------------------------------------------------------

export const getInsuranceDisclaimerLinkLabel = (
    commonSettings: CommonSettingsType,
    uscSettings: UscSettingsType,
    aemComponent: AemFmComponent,
    insuranceQuote: UscInsuranceQuoteType | null,
    context: UscContext = UscContext.Used,
): string => {
    const insurancePriceFormat = getAemInsuranceRateFormat(commonSettings, aemComponent, context);

    const disclaimerLabel = formatUscInsurancePriceAsObject(
        commonSettings,
        uscSettings,
        insuranceQuote,
        insurancePriceFormat,
    ).disclaimer;

    return disclaimerLabel || "";
};

export const getInsuranceProductName = (
    commonSettings: CommonSettingsType,
    uscSettings: UscSettingsType,
    aemComponent: AemFmComponent,
    insuranceQuote: UscInsuranceQuoteType | null,
    context: UscContext = UscContext.Used,
): string => {
    const insurancePriceFormat = getAemInsuranceRateFormat(commonSettings, aemComponent, context);

    const productNameLabel = formatUscInsurancePriceAsObject(
        commonSettings,
        uscSettings,
        insuranceQuote,
        insurancePriceFormat,
    ).insuranceType;

    return productNameLabel || "";
};

export const getInsuranceProductPrice = (
    commonSettings: CommonSettingsType,
    uscSettings: UscSettingsType,
    aemComponent: AemFmComponent,
    insuranceQuote: UscInsuranceQuoteType | null,
    context: UscContext = UscContext.Used,
): string => {
    const insurancePriceFormat = getAemInsuranceRateFormat(commonSettings, aemComponent, context);

    const productPriceLabel = formatUscInsurancePriceAsObject(
        commonSettings,
        uscSettings,
        insuranceQuote,
        insurancePriceFormat,
    ).price;

    return productPriceLabel || "";
};
